import React, { useState, useRef, useEffect } from "react";

import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { Box, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import {
  createJournal,
  getJournalById,
  updateJournal,
} from "../../Api/Services/JournalServices";
import { getPetNameByWorkspaceId } from "../../Api/Services/PetServices";
import { getUsersByWorkspaceId } from "../../Api/Services/UserServices";
import useAuth from "../../ContextProvider/AuthProvider";
import {
  FormHeaderBox,
  FormHeaderTitle,
  Body,
  Column,
  ButtonBoxStyle,
  CancelButton,
} from "../../Css";
import { JournalFormValidation } from "../../From/Validation";
import { formatDate } from "../../Utils";
import AutoComplete from "../Common/AutoComplete";
import CustomButton from "../Common/Button";
import FormGroup from "../Common/FormGroup";
import FormLayout from "../Common/FormHeader";
import InputField from "../Common/InputField";
import { showSnackbar } from "../Common/Snackbar";
import TextArea from "../Common/TextArea";
import { useTheme } from "@mui/material/styles";

const defaultValue = {
  note: "",
  date: "",
  petid: "",
  user: "",
};

export default function Journal() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { workspaceId: w_id } = useAuth();
  const theme = useTheme();
  const componentPDF = useRef();

  // Determine if this is edit mode
  const isEditMode = !!id;

  const [initialValue, setInitialValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);
  const maxDate = new Date().toISOString().substr(0, 10);

  const noteDateRef = useRef(null);
  const noteRef = useRef(null);

  // Load existing data for edit mode
  const loadJournalDetails = async () => {
    if (!isEditMode) return;
    
    try {
      const { data } = await getJournalById(id);
      setInitialValue({
        ...initialValue,
        note: data?.data?.note,
        date: formatDate(data?.data?.date),
        petid: data?.data?.petid,
        user: data?.data?.user,
      });
    } catch (error) {
      console.error("error: ", error);
      showSnackbar({
        type: "error",
        message: "Failed to load journal details.",
      });
    }
  };

  const getPetNames = async () => {
    try {
      const { data } = await getPetNameByWorkspaceId(w_id);
      const filteredData = data?.data?.map((item) => {
        return {
          value: item?.id,
          label: item?.name,
        };
      });
      setItems(filteredData);
    } catch (error) {
      console.error("error: ", error);
    }
  };

  const getUsersByWorkspaceID = async () => {
    try {
      const res = await getUsersByWorkspaceId(w_id);
      const filteredData = res?.data?.data?.map((item) => {
        return {
          value: `${item?.first_name} ${item?.last_name}`,
          label: `${item?.first_name} ${item?.last_name}`,
        };
      });
      if (filteredData?.length === 0) {
        <></>;
      } else {
        setUsersOptions(filteredData);
      }
    } catch (error) {
      console.error("error: ", error);
    }
  };

  useEffect(() => {
    getPetNames();
    getUsersByWorkspaceID();
    if (isEditMode) {
      loadJournalDetails();
    }
  }, [w_id, isEditMode]);

  // Create new journal
  const createJournalData = async (values) => {
    try {
      const res = await createJournal(values);
      showSnackbar({
        type: "success",
        message: "Journal information added successfully",
      });
    } catch (err) {
      showSnackbar({
        type: "error",
        message: "Journal information added failed",
      });
      console.error(err);
      throw err;
    }
  };

  // Update existing journal
  const updateJournalData = async (values) => {
    const escapedNote = values?.note.replace(/'/g, "\\'");
    const data = {
      date: values?.date,
      petid: values?.petid,
      note: escapedNote,
      user: values?.user,
    };
    
    try {
      const res = await updateJournal(id, data);
      showSnackbar({
        type: "success",
        message: "Journal information updated successfully",
      });
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Journal information updated failed",
      });
      console.error("error: ", error);
      throw error;
    }
  };

  const handleSubmit = async (values, { isSubmitting }) => {
    try {
      if (isEditMode) {
        await updateJournalData(values);
      } else {
        await createJournalData(values);
      }
      setOpen(true);
    } catch (error) {
   showSnackbar({
    type: "error",
    message: "Journal information added failed",
   })

    } finally {
      isSubmitting(false);
    }
  };

  // Handle print functionality
  const handlePrint = useReactToPrint({
    content: () => componentPDF.current,
    documentTitle: "Journal Information",
    pageStyle: `
      @page { size: A3; margin: 200px; }
      body { margin: 100px; }
      .print-text-field { font-size: 14px; }
    `,
  });

  return (
    <FormLayout title={isEditMode ? "Edit Journal" : "Journal"}>
      <Formik
        initialValues={initialValue}
        onSubmit={handleSubmit}
        validationSchema={JournalFormValidation}
        enableReinitialize
      >
        {({ getFieldProps, values, isSubmitting }) => (
          <Form>
            <Box sx={FormHeaderBox(theme.palette.primary.main)}>
              <Box sx={{ borderBottom: `1px solid ${theme.palette.primary.main}`, width: "100%" }}>
                <Typography variant="h6" sx={FormHeaderTitle}>
                  Journal Details
                </Typography>
              </Box>
              <div className="scroll2" ref={componentPDF} style={{ width: "100%" }}>
                <Body style={{ gap: "10px 20px" }}>
                  <Column>
                    <FormGroup name="petid" label={"Pet Name"} requiredField>
                      <AutoComplete
                        {...getFieldProps("petid")}
                        options={items}
                        value={isEditMode ? items.find(option => option.value === values.petid) : undefined}
                        textFieldStyle={{
                          width: {
                            xs: 250,
                            sm: 443,
                            md: 443,
                            lg: 443,
                            xl: 443,
                          },
                          height: "35px",
                        }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="date" label={"Date of Note"} requiredField>
                      <InputField
                        type="date"
                        {...getFieldProps("date")}
                        inputProps={{ max: maxDate }}
                        inputRef={noteDateRef}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            noteRef.current.focus();
                          }
                        }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="user" label={"User"} requiredField>
                      <AutoComplete
                        {...getFieldProps("user")}
                        options={usersOptions}
                        value={isEditMode ? usersOptions.find(option => option.value === values.user) : undefined}
                        textFieldStyle={{
                          width: {
                            xs: 250,
                            sm: 443,
                            md: 443,
                            lg: 443,
                            xl: 443,
                          },
                          height: "35px",
                        }}
                      />
                    </FormGroup>
                  </Column>
                </Body>
                <Body style={{ gap: "10px 20px", marginTop: "10px" }}>
                  <Column>
                    <FormGroup name="note" label={"Notes"} requiredField>
                      <TextArea
                        rows={7}
                        {...getFieldProps("note")}
                        inputRef={noteRef}
                      />
                    </FormGroup>
                  </Column>
                </Body>
              </div>
              <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                <Box display="flex">
                  <CustomButton
                    label="Cancel"
                    type="button"
                    style={CancelButton(`${theme.palette.primary.main}`)}
                    onClick={() => navigate(-1)}
                  />
                  <CustomButton
                    label={isSubmitting ? (isEditMode ? "Saving..." : "Submitting...") : (isEditMode ? "Save" : "Submit")}
                    disabled={isSubmitting}
                    type="submit"
                  />
                  {isEditMode && (
                    <CustomButton
                      startIcon={<LocalPrintshopOutlinedIcon />}
                      onClick={handlePrint}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </FormLayout>
  );
}
